/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.clientVariantUpdates = this.dataset.clientVariantUpdates === 'true';
      this.dynamicAvailability = this.dataset.availability !== 'false';
      if (this.clientVariantUpdates || this.dynamicAvailability) {
        this.productData = this.getProductData();
        this.variant = this.getSelectedVariant();
        if (!this.variant && !this.clientVariantUpdates) {
          const variantFromJson = this.getVariantData();
          if (variantFromJson?.id) this.variant = variantFromJson;
        }
      } else {
        this.variant = this.getVariantData();
      }
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (this.shouldRefreshClientAvailability()) {
        this.refreshClientAvailability({ syncSelection: true });
      }
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (this.isSyncingNonexistentSelection) return;

      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      if (this.usesClientVariantUpdates()) {
        this.handleVariantChangeClient(evt);
        return;
      }

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        if (this.shouldRefreshClientAvailability()) {
          this.refreshClientAvailability({ syncSelection: false });
        }
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"], input[name="id[]"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    usesClientVariantUpdates() {
      // Only use the explicit data-client-variant-updates flag — do NOT auto-detect
      // convx-addons, because that flag is already set via client_variant_updates
      // in main-product.liquid when addons are present.
      return this.dataset.clientVariantUpdates === 'true'
        || (!this.quickBuyContainer
          && !this.featuredProductContainer
          && Boolean(this.section.querySelector('convx-addons')));
    }

    shouldRefreshClientAvailability() {
      return Boolean(this.productData?.product)
        && (this.clientVariantUpdates || this.dynamicAvailability);
    }

    /**
     * Variants without SKU are placeholders and must not appear as selectable options.
     * @param {object} variant
     * @returns {boolean}
     */
    static isSelectableVariant(variant) {
      return Boolean((variant.sku || '').trim());
    }

    /**
     * Convx PDPs with FBT addons: update variant state in place (1.6-style) so
     * sibling blocks are not torn down by Section Rendering API.
     * @param {object} evt - Event object.
     */
    handleVariantChangeClient(evt) {
      // Client path updates in place (no Section Rendering). Avoid toggling
      // is-variant-freezing / minHeight — that forced a repaint of the open FBT
      // accordion on every option change. Panel transitions are already disabled
      // via .product-info:has(convx-addons) .convx__addons-panel { transition: none }.
      const addons = this.section.querySelector('convx-addons');
      const addonsHeight = addons ? addons.offsetHeight : 0;
      if (addons && addonsHeight > 0) {
        addons.style.minHeight = `${addonsHeight}px`;
      }

      this.productData = this.productData || this.getProductData();
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateVariantInput();
      }

      // Keep announceChange/URL/BestBuy listeners running even if one UI updater throws
      // (e.g. pickup-availability without getAvailability on Enterprise 2.3).
      try {
        this.refreshClientAvailability({ syncSelection: true });
        this.updateStatusClasses();
        this.updateAddToCartButton();
        this.updateSwatchLabel();
        this.updatePriceClient();
        this.updateWeightClient();
        this.updateBarcodeClient();
        this.updateSkuClient();
        this.updateMediaClient();
        this.updateBackorderTextClient();
        this.updatePickupAvailabilityClient();
        this.updateProductLabelsClient();
      } catch (err) {
        console.error('[variant-picker] client variant UI update failed', err);
      }

      if (addons) {
        // Keep open panels expanded across the option-list reflow, then release the lock.
        addons.preserveOpenPanels?.();
        requestAnimationFrame(() => {
          addons.style.minHeight = '';
          addons.preserveOpenPanels?.();
        });
      }

      if (!evt.detail?.noFocus) {
        document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)
          ?.focus({ preventScroll: true });
      }

      this.announceChange();

      if (typeof window.__kcSyncSoldOutUi === 'function') {
        window.__kcSyncSoldOutUi(this.variant);
      } else if (typeof window.__kcSyncKlaviyoBis === 'function') {
        window.__kcSyncKlaviyoBis(this.variant);
      }
    }

    /**
     * Updates the availability status of an option (client-side path).
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Recompute option availability and optionally move selection off nonexistent values.
     * @param {object} [opts]
     * @param {boolean} [opts.syncSelection=false] - Auto-select a valid option if current is nonexistent.
     */
    refreshClientAvailability({ syncSelection = false } = {}) {
      this.updateAvailabilityClient();
      if (!syncSelection) return;

      if (this.syncSelectionAwayFromNonexistent()) {
        this.variant = this.getSelectedVariant();
        if (this.variant) {
          this.updateVariantInput();
        }
        this.updateAvailabilityClient();
        this.updateSwatchLabel();
      }
    }

    /**
     * If the checked/selected option is nonexistent for the current combination,
     * select the first existing option in that group (prefer in-stock).
     * @returns {boolean} Whether any selection changed.
     */
    syncSelectionAwayFromNonexistent() {
      let didChange = false;

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]'
          );
          if (!selected?.classList.contains('is-nonexistent')) return;

          const next =
            selector.querySelector(
              '.custom-select__option.js-option:not(.is-nonexistent):not(.is-unavailable):not([data-value=""])'
            ) ||
            selector.querySelector(
              '.custom-select__option.js-option:not(.is-nonexistent):not([data-value=""])'
            );
          if (!next) return;

          const customSelect = next.closest('custom-select');
          if (customSelect?.selectOption) {
            this.isSyncingNonexistentSelection = true;
            try {
              customSelect.selectOption(next);
            } finally {
              this.isSyncingNonexistentSelection = false;
            }
            didChange = true;
          }
          return;
        }

        const selected = selector.querySelector('input.js-option:checked');
        if (!selected?.classList.contains('is-nonexistent')) return;

        const next =
          selector.querySelector('input.js-option:not(.is-nonexistent):not(.is-unavailable)') ||
          selector.querySelector('input.js-option:not(.is-nonexistent)');
        if (!next || next === selected) return;

        selected.checked = false;
        next.checked = true;
        didChange = true;
      });

      return didChange;
    }

    updateAvailabilityClient() {
      if (this.dataset.availability === 'false' || !this.productData?.product) return;

      // Use downward cascading: earlier options stay visible; later options are
      // filtered by the selection above (e.g. Type stays Fully Assembled + ISO,
      // Color then shows only Black/White or only ISO colors).
      // "selection" would hide Fully Assembled ISO when Color=Black is selected.
      const selectedOptions = this.getSelectedOptions();

      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = selector.querySelectorAll('.js-option:not([data-value=""])');
        options.forEach((option) => {
          const optionValue =
            selector.dataset.selectorType === 'dropdown'
              ? option.dataset.value
              : option.value;
          let variantsExist = false;
          let variantsAvailable = false;

          this.productData.product.variants.forEach((variant) => {
            if (!VariantPicker.isSelectableVariant(variant)) return;

            let matches = 0;
            for (let i = 0; i < selectorIndex; i += 1) {
              if (
                selectedOptions[i] === null ||
                variant.options[i] === selectedOptions[i]
              ) {
                matches += 1;
              }
            }
            if (
              variant.options[selectorIndex] === optionValue &&
              matches === selectorIndex
            ) {
              variantsExist = true;
              if (variant.available) {
                variantsAvailable = true;
              }
            }
          });

          VariantPicker.updateOptionAvailability(
            option,
            variantsExist,
            variantsAvailable
          );
        });
      });
    }

    updatePriceClient() {
      this.price = this.price || this.section.querySelector('.product-info__price .price, .product-info__price > .price');
      if (!this.price || !this.productData?.formatted) return;

      const selectedOptions = this.getSelectedOptions();
      const preSelection = selectedOptions.indexOf(null) === -1 && !this.variant;

      let variant = this.variant;
      if (preSelection) {
        variant = this.productData.product.variants[0];
        for (let i = 1; i < this.productData.product.variants.length; i += 1) {
          if (this.productData.product.variants[i].price < variant.price) variant = this.productData.product.variants[i];
        }
      }

      const priceCurrentEl = this.price.querySelector('.price__current .js-value');
      const priceWasEl = this.price.querySelector('.price__was .js-value');
      const unitPriceEl = this.price.querySelector('.unit-price');
      const formatted = variant ? this.productData.formatted[variant.id] : null;

      if (this.variant && formatted) {
        if (priceCurrentEl) priceCurrentEl.innerHTML = formatted.price;
        if (priceWasEl) priceWasEl.innerHTML = formatted.compareAtPrice || '';
        if (unitPriceEl) unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle('price--on-sale', this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      const priceDefault = this.price.querySelector('.price__default');
      const priceNoVariant = this.price.querySelector('.price__no-variant');
      const priceFrom = this.price.querySelector('.price__from');
      if (priceDefault) priceDefault.hidden = !this.variant && !preSelection;
      if (priceNoVariant) priceNoVariant.hidden = Boolean(this.variant) || preSelection;
      if (priceFrom) priceFrom.hidden = !preSelection;
    }

    updateWeightClient() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (!this.weights.length || !this.productData?.formatted) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.productData.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    updateMediaClient() {
      if (!this.variant) return;

      const mediaGallery = this.section.querySelector('media-gallery');
      if (!mediaGallery) return;

      // 1. Try variant-level featured_media (set by merchant for color variants)
      if (this.variant.featured_media) {
        const variantMedia = mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          mediaGallery.customSetActiveMedia(variantMedia, true);
          return;
        }
      }

      // 2. Fallback: use variant image_id to find matching media
      if (this.variant.image_id) {
        const variantMedia = mediaGallery.querySelector(
          `[data-media-id="${this.variant.image_id}"]`
        );
        if (variantMedia) {
          mediaGallery.customSetActiveMedia(variantMedia, true);
          return;
        }
      }

      // 3. Last resort: variant image src matching
      if (this.variant.featured_image && this.variant.featured_image.src) {
        const variantSrc = this.variant.featured_image.src.replace(/^https?:/, '');
        const allMedia = mediaGallery.querySelectorAll('[data-media-id]');
        for (const media of allMedia) {
          const img = media.querySelector('img');
          if (img && img.src.replace(/^https?:/, '').includes(variantSrc.split('?')[0])) {
            mediaGallery.customSetActiveMedia(media, true);
            return;
          }
        }
      }
    }

    updateBarcodeClient() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (!this.barcodes.length) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    updateSkuClient() {
      this.skus = this.skus || this.section.querySelectorAll('.product-sku__value');
      if (!this.skus.length) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.skus.forEach((sku) => {
        sku.textContent = skuAvailable ? this.variant.sku : '';
        sku.parentNode.hidden = !skuAvailable;
      });
    }

    updateBackorderTextClient() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder || !this.productData?.formatted) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const inventory = this.productData.formatted[this.variant.id].inventory;

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          if (backorderProdEl && prodTitleEl) {
            backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          }
          hideBackorder = false;
        }
      }

      // Use visibility+height collapse instead of 'hidden' attribute (display:none)
      // to preserve space and prevent the FBT section below from jumping.
      // Only write styles when state changes to avoid layout thrashing.
      const isHidden = this.backorder.dataset.kcBackorderHidden === 'true';
      if (hideBackorder === isHidden && this.backorder.dataset.fbtBackorderPatched) {
        return;
      }
      this.backorder.dataset.fbtBackorderPatched = '1';
      this.backorder.dataset.kcBackorderHidden = hideBackorder ? 'true' : 'false';
      if (hideBackorder) {
        this.backorder.style.visibility = 'hidden';
        this.backorder.style.height = '0';
        this.backorder.style.margin = '0';
        this.backorder.style.overflow = 'hidden';
        this.backorder.removeAttribute('hidden');
      } else {
        this.backorder.style.visibility = '';
        this.backorder.style.height = '';
        this.backorder.style.margin = '';
        this.backorder.style.overflow = '';
        this.backorder.removeAttribute('hidden');
      }
    }

    updatePickupAvailabilityClient() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        // Enterprise 2.3 pickup markup may exist without pickup-availability.js.
        if (typeof this.pickUpAvailability.getAvailability === 'function') {
          this.pickUpAvailability.getAvailability(this.variant.id);
        }
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    updateProductLabelsClient() {
      if (typeof window.__kcSyncSoldOutUi === 'function') {
        window.__kcSyncSoldOutUi(this.variant);
        return;
      }

      const productRoot = this.section.querySelector('.product.js-product') || this.section;
      const showSoldOut = Boolean(this.variant && !this.variant.available);

      productRoot.querySelectorAll('.product-label--sold-out').forEach((label) => {
        label.hidden = !showSoldOut;
        label.style.display = showSoldOut ? '' : 'none';
        const wrapper = label.closest('.product-label-wrapper');
        if (wrapper) {
          wrapper.hidden = !showSoldOut;
          wrapper.style.display = showSoldOut ? '' : 'none';
        }
      });
    }

    updateVariantInput() {
      const forms = this.section.querySelectorAll(
        '.js-product-form-main, .js-instalments-form, .js-product-form, .buy-now-form, .add-to-cart-form'
      );

      forms.forEach((form) => {
        // Only the main product variant id — never FBT/addon `name="id[]"` checkboxes
        // (those are associated via form="" and would be matched first by id[]).
        const input =
          form.querySelector('input[name="id"][type="hidden"]') ||
          form.querySelector('input[name="id"]:not([type="checkbox"])');
        if (!input || !this.variant) return;
        if (String(input.value) === String(this.variant.id) && !input.disabled) {
          if (form.classList.contains('buy-now-form')) {
            form.dataset.variantId = this.variant.id;
          }
          return;
        }
        input.value = this.variant.id;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        if (form.classList.contains('buy-now-form')) {
          form.dataset.variantId = this.variant.id;
        }
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        this.syncBuyNowButtons(true);
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
          this.syncBuyNowButtons(true);
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
          this.syncBuyNowButtons(false);
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        this.syncBuyNowButtons(true);
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        this.syncBuyNowButtons(true);
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
      this.syncBuyNowButtons(false);
    }

    /**
     * Keep Quick Add buy-now submit buttons aligned with ATC disabled state.
     * Scoped to quick-buy drawer only — must not touch PDP / section-3 buy-now.
     * @param {boolean} disabled
     */
    syncBuyNowButtons(disabled) {
      if (!this.quickBuyContainer) return;
      this.quickBuyContainer.querySelectorAll('.buy-now-form [name="add"]').forEach((btn) => {
        btn.disabled = disabled;
      });
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      // Do not strip ?variant= when the current selection has no matched variant.
      if (!this.variant?.id) return;
      window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Gets the product data (client-side variant update path).
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get selected variant data (client-side variant update path).
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const productData = this.productData || this.getProductData();
      if (!productData?.product) return null;

      const selectedOptions = this.getSelectedOptions();
      return productData.product.variants.find(
        (v) => VariantPicker.isSelectableVariant(v)
          && v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
